import { html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { statusBadge } from '@vitesse/base-ui/components/templates';
import { fmtAgo, fmtDateTime, fmtTime } from '@vitesse/base-ui/format';
import { t } from '@vitesse/base-ui/i18n';
import type { IntegrationManager } from '../../managers/integration-manager';
import type { ModelView } from '../../../core/domain/model';
import { forwardColor, type ModelForward } from './model-forwards';
import { canReadBack, fmtDelay, SENT_LIMIT, sentQuery, statePaths, toSentRows, type SentRow } from './model-streaming';

/*
 * <vitesse-model-streaming> — THE STREAMING TAB OF THE MODEL PAGE.
 *
 * 1 card for each Forward that sends the states and the status of this model to a table:
 *   - its status: the integration, the table, the state, the last write and the last error;
 *   - Buffered: the values that the table does not have yet (the `lag` of the Forward);
 *   - Sent: the last values in the table, read back with a read-only query.
 *
 * The status comes from the integration roster, which the page already holds. Only Sent costs a
 * call: 1 query for each Forward each POLL_MS, and only while the tab shows and the page is
 * visible. The page removes this element when another tab shows, and that stops the polls.
 */

/** How often the tab reads the tables again. A Forward writes a batch each 1 s. */
const POLL_MS = 3000;

/** The time of a value of today, to the ms: 2 values of 1 batch are ms apart. */
const TIME: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  fractionalSecondDigits: 3,
  hour12: false,
};
/** The time of a value of another day also names the day. */
const DAY_TIME: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', ...TIME };

/** What the tab read from the table of 1 Forward. */
interface Sent {
  rows: SentRow[];
  /** Why the last read failed, or ''. The rows of the read before stay. */
  error: string;
}

@customElement('vitesse-model-streaming')
export class ModelStreamingTab extends LitElement {
  @property({ attribute: false }) model?: ModelView;
  @property({ attribute: false }) forwards: ModelForward[] = [];
  @property({ attribute: false }) integrations?: IntegrationManager;
  @property() gatewayId = '';

  /** By forwardKey. No entry until the first read of that Forward answers. */
  @state() private sent = new Map<string, Sent>();
  /** The model and the Forwards that `sent` is for. A change clears it, and a read for the old ones is dropped. */
  private readsFor = '';
  private readonly inFlight = new Set<string>();
  private timer?: ReturnType<typeof setInterval>;

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM → global token-based SCSS applies
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.timer = setInterval(() => void this.poll(), POLL_MS);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    clearInterval(this.timer);
    this.timer = undefined;
  }

  protected willUpdate(changed: PropertyValues): void {
    // The page passes a new model on each live tick. Only a change of what the reads ask clears the
    // rows and reads again.
    if (changed.has('model') || changed.has('forwards')) {
      const key = this.readsKey();
      if (key !== this.readsFor) {
        this.readsFor = key;
        this.sent = new Map();
        void this.poll();
      }
    }
  }

  private readsKey(): string {
    const m = this.model;
    if (!m) return '';
    return JSON.stringify([statePaths(m), this.forwards.map((f) => [forwardKey(f), f.forward.target])]);
  }

  private async poll(): Promise<void> {
    const m = this.model;
    const integrations = this.integrations;
    if (!m || !integrations || document.hidden) return;
    const asked = this.readsFor;
    const paths = statePaths(m);
    await Promise.all(
      this.forwards
        .filter((f) => canReadBack(f.integration))
        .map(async (f) => {
          const key = forwardKey(f);
          // A slow table must not collect reads: the next poll skips a Forward whose read still runs.
          if (this.inFlight.has(key)) return;
          this.inFlight.add(key);
          const { statement, args } = sentQuery(f.forward.target, paths);
          let next: Sent;
          try {
            next = { rows: toSentRows(m.id, await integrations.query(f.integration.id, statement, args, SENT_LIMIT)), error: '' };
          } catch (e) {
            next = { rows: this.sent.get(key)?.rows ?? [], error: e instanceof Error ? e.message : String(e) };
          } finally {
            this.inFlight.delete(key);
          }
          if (this.readsFor !== asked) return; // the model or the Forwards changed while it ran
          this.sent = new Map(this.sent).set(key, next);
        }),
    );
  }

  private openIntegration(integrationId: string): void {
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'integration', gatewayId: this.gatewayId, integrationId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  protected render() {
    return html`<div class="model-streaming">
      <p class="model-forwards-intro">${t('model.forwardsIntro')}</p>
      <ul class="model-forwards">
        ${this.forwards.map((f) => this.renderForward(f))}
      </ul>
    </div>`;
  }

  private renderForward(f: ModelForward) {
    const i = f.integration;
    const st = f.status;
    return html`<li class="model-forward">
      <div class="model-forward-head">
        <button class="base-link" title=${t('model.forwardOpen')} @click=${() => this.openIntegration(i.id)}>
          ${i.displayName || i.id}
        </button>
        ${i.typeLabel ? html`<span class="model-forward-type">${i.typeLabel}</span>` : nothing}
        ${statusBadge(forwardColor(f.state), forwardStateLabel(f.state))}
      </div>
      <div class="model-forward-meta">
        <span>${t('model.forwardTable', { table: f.forward.target })}</span>
        ${
          st
            ? html`<span
                >${st.lastWriteMs > 0 ? t('model.forwardLastWrite', { ago: fmtAgo(st.lastWriteMs) }) : t('model.forwardNoWrite')}</span
              >`
            : nothing
        }
      </div>
      ${
        st?.lastError
          ? html`<div class="model-forward-error">
              ${t('model.forwardLastError', { ago: fmtAgo(st.lastErrorMs), error: st.lastError })}
            </div>`
          : nothing
      }
      <h4 class="model-streaming-heading">${t('model.streamingBuffered')}</h4>
      <p class="model-streaming-note">
        ${
          !st
            ? t('model.streamingBufferedUnknown')
            : st.lag === 0
              ? t('model.streamingBufferedNone')
              : t('model.streamingBufferedSome', { n: st.lag })
        }
      </p>
      <h4 class="model-streaming-heading">${t('model.streamingSent')}</h4>
      ${this.renderSent(f)}
    </li>`;
  }

  private renderSent(f: ModelForward) {
    if (!canReadBack(f.integration)) {
      return html`<p class="model-streaming-note">
        ${t('model.streamingNoReadBack', { type: f.integration.typeLabel || f.integration.type })}
      </p>`;
    }
    const s = this.sent.get(forwardKey(f));
    if (!s) return html`<p class="model-streaming-note">${t('model.streamingReading')}</p>`;
    return html`
      ${s.error ? html`<div class="model-forward-error">${t('model.streamingReadFailed', { error: s.error })}</div>` : nothing}
      ${
        s.rows.length === 0
          ? html`<p class="model-streaming-note">${t('model.streamingNoRows')}</p>`
          : html`<p class="model-streaming-note">${t('model.streamingSentNote', { n: SENT_LIMIT })}</p>
              <div class="model-streaming-scroll">
                <table class="model-streaming-table">
                  <thead>
                    <tr>
                      <th>${t('model.streamingColMachine')}</th>
                      <th>${t('model.streamingColValue')}</th>
                      <th>${t('model.streamingColValueTime')}</th>
                      <th>${t('model.streamingColTableTime')}</th>
                      <th class="num">${t('model.streamingColDelay')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${s.rows.map(
                      (r) =>
                        html`<tr>
                          <td>${this.machineLabel(r)}</td>
                          <td>${r.value}</td>
                          <td title=${fmtDateTime(r.valueMs)}>${when(r.valueMs)}</td>
                          <td title=${Number.isFinite(r.tableMs) ? fmtDateTime(r.tableMs) : ''}>
                            ${Number.isFinite(r.tableMs) ? when(r.tableMs) : '—'}
                          </td>
                          <td class="num">${fmtDelay(r.tableMs - r.valueMs) || '—'}</td>
                        </tr>`,
                    )}
                  </tbody>
                </table>
              </div>`
      }
    `;
  }

  private machineLabel(r: SentRow): string {
    switch (r.kind) {
      case 'status':
        return t('model.streamingStatus');
      case 'machine':
        return r.machine;
      default:
        return this.model?.name || this.model?.id || '';
    }
  }
}

/** 1 Forward of 1 integration. */
function forwardKey(f: ModelForward): string {
  return `${f.integration.id}/${f.forward.name}`;
}

/** The time of a value: to the ms, with the day when it is not today. */
function when(ms: number): string {
  return fmtTime(ms, new Date(ms).toDateString() === new Date().toDateString() ? TIME : DAY_TIME);
}

/** The label of a Forward state (a switch, so t() sees literal keys). */
export function forwardStateLabel(state: string): string {
  switch (state) {
    case 'running':
      return t('model.forwardRunning');
    case 'starting':
      return t('model.forwardStarting');
    case 'error':
      return t('model.forwardError');
    case 'stopped':
      return t('model.forwardStopped');
    case 'not reported yet':
      return t('model.forwardUnreported');
    default:
      return state;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-model-streaming': ModelStreamingTab;
  }
}
